import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import MissingProgressBar from "../Missing/MissingProgressBar";
import MissingStatusChip from "../Missing/MissingStatusChip";
import OmissionReasonChip from "./OmissionReasonChip";
import PriceHistoryOmittedDetailDialog from "./PriceHistoryOmittedDetailDialog";
import PriceHistoryOmittedToolbar from "./PriceHistoryOmittedToolbar";
import { OMISSION_REASON_CHOICES } from "./omissionReasons";
import {
  fetchOmittedTaskRunDetails,
  fetchPriceHistoryOmittedRows,
  fetchPriceHistoryOmittedSummary,
  priceHistoryOmittedConfigured,
} from "../../../services/priceHistoryOmitted";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatPriceCell = (row) => {
  if (row.raw_price == null || row.raw_price === "") {
    return "-";
  }
  const unitBit =
    row.raw_unit_price != null && row.raw_unit_price !== ""
      ? ` (${row.raw_unit_price}${row.raw_unit ? `/${row.raw_unit}` : ""})`
      : "";
  return `$${row.raw_price}${unitBit}`;
};

const PriceHistoryOmittedReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState("pending");
  const [code, setCode] = useState("all");
  const [sortBy, setSortBy] = useState("created_desc");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    remaining: 0,
    byCode: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [taskRun, setTaskRun] = useState(null);
  const [loadingTaskRun, setLoadingTaskRun] = useState(false);

  const loadData = useCallback(async () => {
    if (!priceHistoryOmittedConfigured()) {
      setError(
        "Price omitted report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
      );
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [rowsRes, summaryRes] = await Promise.all([
        fetchPriceHistoryOmittedRows({
          page: page + 1,
          limit: rowsPerPage,
          status,
          code,
          sortBy,
        }),
        fetchPriceHistoryOmittedSummary(),
      ]);
      setRows(rowsRes.rows);
      setTotal(rowsRes.total);
      setSummary(summaryRes);
    } catch (e) {
      setError(e.message || "Failed to load price history omitted data.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, code, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let active = true;

    async function loadTaskRun() {
      if (!selected?.task_run_id) {
        setTaskRun(null);
        setLoadingTaskRun(false);
        return;
      }

      setLoadingTaskRun(true);
      setTaskRun(null);
      try {
        const nextTaskRun = await fetchOmittedTaskRunDetails(selected.task_run_id);
        if (!active) return;
        setTaskRun(nextTaskRun);
      } catch (_) {
        if (!active) return;
        setTaskRun(null);
      } finally {
        if (active) {
          setLoadingTaskRun(false);
        }
      }
    }

    loadTaskRun();
    return () => {
      active = false;
    };
  }, [selected]);

  const pct =
    summary.total > 0 ? Math.round((summary.resolved / summary.total) * 100) : 0;

  const codeChips = OMISSION_REASON_CHOICES.map((entry) => ({
    ...entry,
    count: summary.byCode?.[entry.code] || 0,
  })).filter((entry) => entry.count > 0);

  const handleCloseDetail = () => {
    setSelected(null);
    setTaskRun(null);
    setLoadingTaskRun(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "#090909",
            fontSize: "0.9rem",
            textTransform: "capitalize",
            mb: 0.5,
          }}
        >
          Price History / Omitted
        </Typography>
        <Typography
          sx={{
            fontWeight: 300,
            color: "#090909",
            fontSize: "0.8rem",
            mb: 1.5,
          }}
        >
          Audit log of analysis items omitted from price history, with structured reason codes.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
          <Chip size="small" label={`Total ${summary.total}`} />
          <Chip size="small" color="warning" label={`Pending ${summary.pending}`} />
          <Chip size="small" color="success" label={`Resolved ${summary.resolved}`} />
          <Chip
            size="small"
            color={summary.remaining > 0 ? "warning" : "success"}
            label={`Remaining ${summary.remaining}`}
          />
          <Chip size="small" variant="outlined" label={`${pct}% complete`} />
        </Stack>
        <MissingProgressBar value={pct} />

        {codeChips.length ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
            {codeChips.map((entry) => (
              <Chip
                key={entry.code}
                size="small"
                variant={code === entry.code ? "filled" : "outlined"}
                color={code === entry.code ? "primary" : "default"}
                label={`${entry.label} ${entry.count}`}
                onClick={() => {
                  setPage(0);
                  setCode((prev) => (prev === entry.code ? "all" : entry.code));
                }}
                title={entry.meaning}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <PriceHistoryOmittedToolbar
        status={status}
        onStatusChange={(nextStatus) => {
          setPage(0);
          setStatus(nextStatus);
        }}
        code={code}
        onCodeChange={(nextCode) => {
          setPage(0);
          setCode(nextCode);
        }}
        sortBy={sortBy}
        onSortChange={(nextSortBy) => {
          setPage(0);
          setSortBy(nextSortBy);
        }}
      />

      <Table
        sx={{
          width: "100%",
          border: "1px solid #eaeaea",
          backgroundColor: "#fff",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Retailer</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Record At</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelected(row);
                setError("");
              }}
            >
              <TableCell>
                <MissingStatusChip status={row.status} />
              </TableCell>
              <TableCell>
                <OmissionReasonChip code={row.code} />
              </TableCell>
              <TableCell>{row.raw_product_name || "-"}</TableCell>
              <TableCell>{row.raw_retailer || "-"}</TableCell>
              <TableCell>{row.raw_barcode || "-"}</TableCell>
              <TableCell>{formatPriceCell(row)}</TableCell>
              <TableCell>{formatDate(row.record_at)}</TableCell>
              <TableCell>{formatDate(row.date_created)}</TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2">No omitted price history records found.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <PriceHistoryOmittedDetailDialog
        selected={selected}
        taskRun={taskRun}
        loadingTaskRun={loadingTaskRun}
        onClose={handleCloseDetail}
      />
    </Box>
  );
};

export default PriceHistoryOmittedReport;
